/**
 * WorkspaceOverview
 * -----------------
 * The "settings dashboard" for a client (business_owner / manager). Gives
 * them a single page that summarises everything about their workspace:
 *
 *   • Workspace name / slug / industry / created-at
 *   • Plan + status (read-only — only Kairosa super-admin can change)
 *   • Active modules
 *   • Team members with role badges (invite is delegated to /app/settings/team)
 *   • Custom domain link
 *   • Support contact CTA
 *
 * Deliberately NOT the super-admin Parent Dashboard — clients cannot see any
 * other workspace, plan pricing, or the vault. This is scoped to their own
 * tenant only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Users, CalendarDays, ShieldCheck, Globe, Palette, Settings,
  ExternalLink, LifeBuoy, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";


function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}


function StatCard({ label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl">{value ?? "—"}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}


function CopyableChip({ value, label, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  };
  return (
    <button
      onClick={copy}
      title={label}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 hover:bg-secondary transition px-2.5 py-1 text-xs font-mono"
    >
      {value}
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
}


export default function WorkspaceOverview() {
  const [tenant, setTenant] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, tm] = await Promise.all([
          api.get("/tenant/me"),
          api.get("/tenant/team"),
        ]);
        setTenant(t.data);
        setTeam(tm.data.items || tm.data || []);
      } catch (e) {
        toast.error("Could not load workspace overview");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const enabledModules = useMemo(
    () => (tenant?.modules || []).filter((m) => m !== "core"),
    [tenant]
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="text-sm text-muted-foreground">Loading your workspace…</div>
      </div>
    );
  }
  if (!tenant) return null;

  const planLabel = (tenant.plan || "trial").toUpperCase();
  const statusLabel = (tenant.status || "active").toLowerCase();
  const isPaused = statusLabel === "paused";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="workspace-overview">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Workspace · Overview
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-1 flex items-center gap-3">
            <Building2 className="h-7 w-7 text-primary" />
            {tenant.name}
            {isPaused && (
              <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">
                Build stage
              </span>
            )}
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <CopyableChip value={tenant.slug} label="Slug" testId="ws-slug-chip" />
            <span className="text-xs text-muted-foreground">
              {tenant.industry ? `${tenant.industry} · ` : ""}created {formatDate(tenant.created_at)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/app/settings/branding">
            <Button variant="outline" className="rounded-full gap-1.5" data-testid="ws-branding-cta">
              <Palette className="h-4 w-4" /> Branding
            </Button>
          </Link>
          <Link to="/app/settings/modules">
            <Button variant="outline" className="rounded-full gap-1.5" data-testid="ws-modules-cta">
              <Settings className="h-4 w-4" /> Modules
            </Button>
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Plan"
          value={planLabel}
          hint="Managed by the platform admin"
          testId="ws-stat-plan"
        />
        <StatCard
          label="Status"
          value={statusLabel === "active" ? "Live" : (statusLabel === "paused" ? "Paused" : "—")}
          hint={isPaused ? "Contact your admin to go live" : "Everything is running"}
          testId="ws-stat-status"
        />
        <StatCard
          label="Active Modules"
          value={enabledModules.length}
          hint={enabledModules.length ? "core is always on (not counted)" : "Enable modules in Settings"}
          testId="ws-stat-modules"
        />
        <StatCard
          label="Team Members"
          value={team.length}
          hint={team.length === 1 ? "You're solo — invite in Team" : "Invite in Team & Roles"}
          testId="ws-stat-team"
        />
      </div>

      {/* Modules card */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="ws-modules-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h2 className="font-display text-xl">Modules on your workspace</h2>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
            · what's unlocked
          </span>
        </div>
        {enabledModules.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center">
            <div className="text-sm text-muted-foreground">No modules enabled yet.</div>
            <div className="text-xs text-muted-foreground mt-1">
              Ask your Kairosa admin to attach modules to your plan.
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {enabledModules.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs text-primary capitalize"
                data-testid={`ws-mod-${m}`}
              >
                <Check className="h-3 w-3" /> {m}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Team card */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="ws-team-card">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-primary" />
          <h2 className="font-display text-xl">Team</h2>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
            · people with access to this workspace
          </span>
          <Link to="/app/settings/team" className="ml-auto text-xs text-primary hover:underline" data-testid="ws-team-manage">
            Manage →
          </Link>
        </div>
        <div className="divide-y divide-border">
          {team.slice(0, 8).map((m) => (
            <div key={m.id || m.email} className="py-2 flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-secondary grid place-items-center text-xs font-medium">
                {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{m.name || m.email}</div>
                <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
              </div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {m.role || "member"}
              </span>
            </div>
          ))}
          {team.length === 0 && (
            <div className="py-4 text-sm text-muted-foreground">No team members visible.</div>
          )}
        </div>
      </div>

      {/* Domain + support row */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="ws-domain-card">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg">Domain</h2>
          </div>
          {tenant.custom_domain && tenant.domain_verified ? (
            <div>
              <div className="text-sm">Live at</div>
              <a
                href={`https://${tenant.custom_domain}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:underline mt-1"
                data-testid="ws-domain-link"
              >
                {tenant.custom_domain} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Serving under <code className="text-xs px-1 rounded bg-secondary">{tenant.slug}.kairosa.io</code>.
              <Link to="/app/settings/domain" className="text-primary hover:underline ml-1">
                Configure a custom domain →
              </Link>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5" data-testid="ws-support-card">
          <div className="flex items-center gap-2 mb-2">
            <LifeBuoy className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg">Need help?</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Reach the Kairosa team any time — plan questions, module requests, or a stuck integration.
          </p>
          <a href="mailto:sanbhan1812@gmail.com?subject=Kairosa%20support">
            <Button variant="outline" className="rounded-full gap-1.5 mt-3" data-testid="ws-support-cta">
              <LifeBuoy className="h-4 w-4" /> Contact support
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
